/**
 * m3 echo Frontend - KI-Modelle Tab (Admin)
 *
 * Zeigt verfügbare KI-Modelle mit Health-Status, Aktivierungskontrolle,
 * Provider-Anzeige und Möglichkeit neue Modelle hinzuzufügen/zu löschen.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Divider,
  Typography,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import PricingSection from './PricingSection';
import BenchmarkLineupSection from './BenchmarkLineupSection';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LanguageIcon from '@mui/icons-material/Language';
import { useQueryClient } from '@tanstack/react-query';
import { settingsApi } from '../../services/api';
import { invalidateModelCatalog } from '../../hooks/useModelCatalog';
import { AIModel, AIProvider } from '../../types/api';

const errorDetail = (error: unknown, fallback: string): string => {
  const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  return typeof detail === 'string' ? detail : fallback;
};

const ModelsTab: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  // Erhöhen, damit die Benchmark-Aufstellung nach Modell-/Preisänderungen neu lädt
  // (ohne den ungespeicherten Entwurf zu verwerfen)
  const [lineupReloadSignal, setLineupReloadSignal] = useState(0);
  const reloadLineup = useCallback(() => setLineupReloadSignal((s) => s + 1), []);
  const [statusError, setStatusError] = useState('');

  // Edit Dialog (Anzeigename, Grounding)
  const [editTarget, setEditTarget] = useState<AIModel | null>(null);
  const [editName, setEditName] = useState('');
  const [editGrounding, setEditGrounding] = useState(false);
  const [editError, setEditError] = useState('');

  const [aiModels, setAiModels] = useState<AIModel[]>([]);
  const [providers, setProviders] = useState<AIProvider[]>([]);
  const [testingModel, setTestingModel] = useState<string | null>(null);

  // Add Model Dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newModelCode, setNewModelCode] = useState('');
  const [newDisplayName, setNewDisplayName] = useState('');
  const [newProvider, setNewProvider] = useState('');
  const [newGrounding, setNewGrounding] = useState(false);
  const [addError, setAddError] = useState('');

  // Delete Confirmation Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<AIModel | null>(null);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  // Nach Änderungen an Modellen: eigene Liste, Aufstellung und Katalog-Caches aktualisieren
  const afterModelChange = async () => {
    await loadData();
    reloadLineup();
    invalidateModelCatalog(queryClient);
  };

  const loadData = async () => {
    try {
      const [modelsRes, providersRes] = await Promise.all([
        settingsApi.getAIModels(),
        settingsApi.getProviders(),
      ]);
      setAiModels(modelsRes.data);
      setProviders(providersRes.data);
    } catch (error) {
      console.error('Fehler beim Laden der Modelle:', error);
    }
  };

  const handleTestModelHealth = async (modelCode: string) => {
    try {
      setTestingModel(modelCode);
      await settingsApi.testModelHealth(modelCode);
      // Neuer Health-Status auch in der Aufstellung (Entwurf bleibt erhalten)
      await afterModelChange();
    } catch (error) {
      console.error(`Fehler beim Testen des Modells ${modelCode}:`, error);
    } finally {
      setTestingModel(null);
    }
  };

  const handleToggleModelStatus = async (model: AIModel) => {
    setStatusError('');
    try {
      await settingsApi.updateAIModelStatus(model.model_code, !model.is_active);
      await afterModelChange();
    } catch (error) {
      setStatusError(errorDetail(error, 'Fehler beim Aktualisieren des Modellstatus'));
    }
  };

  const handleAddModel = async () => {
    setAddError('');
    try {
      await settingsApi.createAIModel({
        model_code: newModelCode,
        display_name: newDisplayName,
        provider: newProvider,
        supports_grounding: newGrounding,
      });
      setAddDialogOpen(false);
      setNewModelCode('');
      setNewDisplayName('');
      setNewProvider('');
      setNewGrounding(false);
      await afterModelChange();
    } catch (error) {
      setAddError(errorDetail(error, 'Fehler beim Erstellen des Modells'));
    }
  };

  const handleDeleteModel = async () => {
    if (!deleteTarget) return;
    setDeleteError('');
    try {
      await settingsApi.deleteAIModel(deleteTarget.model_code);
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await afterModelChange();
    } catch (error) {
      setDeleteError(errorDetail(error, 'Fehler beim Löschen des Modells'));
    }
  };

  const openEdit = (model: AIModel) => {
    setEditTarget(model);
    setEditName(model.display_name);
    setEditGrounding(model.supports_grounding);
    setEditError('');
  };

  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setEditError('');
    try {
      await settingsApi.updateAIModel(editTarget.model_code, {
        display_name: editName.trim(),
        supports_grounding: editGrounding,
      });
      setEditTarget(null);
      await afterModelChange();
    } catch (error) {
      setEditError(errorDetail(error, 'Fehler beim Speichern des Modells'));
    }
  };

  const formatTimestamp = (timestamp: string | null): string => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Heute';
    if (diffDays === 1) return 'Gestern';
    if (diffDays < 7) return `vor ${diffDays} Tagen`;
    return date.toLocaleDateString('de-DE');
  };

  const getStatusColor = (status: string | null): string => {
    switch (status) {
      case 'healthy':
        return '#4caf50';
      case 'unhealthy':
        return '#f44336';
      case 'unknown':
      default:
        return '#9e9e9e';
    }
  };

  const getProviderLabel = (provider: string): string => {
    switch (provider) {
      case 'google':
        return 'Google';
      case 'openai':
        return 'OpenAI';
      case 'anthropic':
        return 'Anthropic';
      default:
        return provider;
    }
  };

  const getProviderColor = (provider: string): 'primary' | 'secondary' | 'default' => {
    switch (provider) {
      case 'google':
        return 'primary';
      case 'openai':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // Providers that have both API key AND client implementation
  const availableProviders = providers.filter((p) => p.has_api_key && p.has_client);

  return (
    <>
      <BenchmarkLineupSection reloadSignal={lineupReloadSignal} onApplied={loadData} />
      <Divider sx={{ my: 4 }} />

      {/* Header with Provider Status */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Verfügbare KI-Modelle</Typography>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          disabled={availableProviders.length === 0}
          size="small"
        >
          Modell hinzufügen
        </Button>
      </Box>

      {/* Provider Status Chips */}
      <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        {providers.map((p) => (
          <Chip
            key={p.provider}
            icon={
              <FiberManualRecordIcon
                sx={{ fontSize: 12, color: p.has_api_key ? '#4caf50' : '#9e9e9e' }}
              />
            }
            label={p.name}
            size="small"
            variant="outlined"
            sx={{ opacity: p.has_api_key ? 1 : 0.5 }}
          />
        ))}
      </Box>

      {statusError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStatusError('')}>
          {statusError}
        </Alert>
      )}

      {/* Model List */}
      <List>
        {aiModels.map((model) => (
          <ListItem
            key={model.model_code}
            secondaryAction={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={model.is_active ? 'Aktiv' : 'Inaktiv'}
                  size="small"
                  color={model.is_active ? 'success' : 'default'}
                  onClick={() => handleToggleModelStatus(model)}
                  sx={{ cursor: 'pointer', minWidth: 70 }}
                />
                <Tooltip title="Health Check">
                  <IconButton
                    edge="end"
                    onClick={() => handleTestModelHealth(model.model_code)}
                    disabled={testingModel === model.model_code}
                  >
                    <SyncIcon
                      sx={{
                        animation:
                          testingModel === model.model_code
                            ? 'spin 1s linear infinite'
                            : 'none',
                        '@keyframes spin': {
                          from: { transform: 'rotate(0deg)' },
                          to: { transform: 'rotate(360deg)' },
                        },
                      }}
                    />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Name und Websuche bearbeiten">
                  <IconButton edge="end" onClick={() => openEdit(model)}>
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip
                  title={
                    model.deletable === false
                      ? 'Modell wird noch verwendet (Benchmark-Historie oder Layer-Konfiguration) – bitte deaktivieren statt löschen'
                      : 'Modell löschen'
                  }
                >
                  <span>
                    <IconButton
                      edge="end"
                      disabled={model.deletable === false}
                      onClick={() => {
                        setDeleteTarget(model);
                        setDeleteError('');
                        setDeleteDialogOpen(true);
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            }
          >
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
              <FiberManualRecordIcon
                sx={{
                  color: getStatusColor(model.health_status),
                  fontSize: 16,
                }}
              />
            </Box>
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {model.display_name}
                  <Chip
                    label={getProviderLabel(model.provider)}
                    size="small"
                    color={getProviderColor(model.provider)}
                    variant="outlined"
                    sx={{ fontSize: '0.7rem', height: 20 }}
                  />
                  {model.supports_grounding && (
                    <Tooltip title="Unterstützt Web Search Grounding">
                      <LanguageIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    </Tooltip>
                  )}
                </Box>
              }
              secondary={
                <>
                  <Typography component="span" variant="caption" color="text.secondary">
                    {model.model_code}
                  </Typography>
                  {testingModel === model.model_code && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (Testing...)
                    </Typography>
                  )}
                  {model.health_status === 'unhealthy' && testingModel !== model.model_code && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="error.main"
                      sx={{ ml: 1 }}
                    >
                      (Unhealthy)
                    </Typography>
                  )}
                  {model.health_checked_at && testingModel !== model.model_code && (
                    <Typography
                      component="span"
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      • Letzter Check: {formatTimestamp(model.health_checked_at)}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        ))}
      </List>

      {aiModels.length === 0 && (
        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
          Keine Modelle konfiguriert.
        </Typography>
      )}

      {/* Add Model Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Modell hinzufügen</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Anbieter</InputLabel>
              <Select
                value={newProvider}
                label="Anbieter"
                onChange={(e) => {
                  setNewProvider(e.target.value);
                  setNewGrounding(e.target.value === 'google');
                }}
              >
                {availableProviders.map((p) => (
                  <MenuItem key={p.provider} value={p.provider}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Modell-Code (API-Bezeichner)"
              value={newModelCode}
              onChange={(e) => setNewModelCode(e.target.value)}
              placeholder="z.B. gemini-2.5-flash-exp"
              helperText="Der API-Bezeichner, wie er an die API gesendet wird"
              fullWidth
            />
            <TextField
              label="Anzeigename"
              value={newDisplayName}
              onChange={(e) => setNewDisplayName(e.target.value)}
              placeholder="z.B. Gemini 2.5 Flash Exp"
              helperText="Name, der in der UI angezeigt wird"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch checked={newGrounding} onChange={(e) => setNewGrounding(e.target.checked)} />
              }
              label="Websuche/Grounding"
            />
            {addError && <Alert severity="error">{addError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleAddModel}
            variant="contained"
            disabled={!newModelCode || !newDisplayName || !newProvider}
          >
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Model Dialog */}
      <Dialog
        open={editTarget !== null}
        onClose={() => setEditTarget(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Modell bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Anzeigename"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              helperText={editTarget?.model_code}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch checked={editGrounding} onChange={(e) => setEditGrounding(e.target.checked)} />
              }
              label="Websuche/Grounding"
            />
            {editError && <Alert severity="error">{editError}</Alert>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTarget(null)}>Abbrechen</Button>
          <Button onClick={handleSaveEdit} variant="contained" disabled={!editName.trim()}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Modell löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie das Modell <strong>{deleteTarget?.display_name}</strong> (
            {deleteTarget?.model_code}) wirklich löschen? Diese Aktion kann nicht rückgängig gemacht
            werden.
          </DialogContentText>
          {deleteError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteModel} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Pricing Section */}
      <Divider sx={{ my: 4 }} />
      <PricingSection onSaved={reloadLineup} />
    </>
  );
};

export default ModelsTab;
